import { v3 } from "../short3";
import * as Maths3 from "../Maths3";

import { Line3, Matrix3, Matrix4, Raycaster, Sphere, Vector2, Vector3 } from "three";

declare const STAGE: any;

const ConeStatics = {
  m1: new Matrix3(),
  v1: v3(),
  CmV: v3(),
  D: v3(),
};

/**
 * Ein Kegel
 */
export class Cone {
  public origin: Vector3;
  public direction: Vector3;
  public alpha: number;
  public height: number;

  private bottomCenter_: Vector3 | null = null;
  private bottomHullPoint_: Vector3 | null = null;
  private center_: Vector3 | null = null;
  private sphere_: Sphere | null = null;
  private line_: Line3 | null = null;

  public tanAlpha: number;
  public sinAlpha: number;
  public cosAlpha: number;

  /**
   * Ein Kegel, dessen Spitze (top) im origin ist und der sich nach direction öffnet mit Winkel alpha.
   * direction must be normalized. height may be Number.POSITIVE_INFINITY
   */
  constructor(
    origin: Vector3 = v3(0, 0, 0),
    direction: Vector3 = v3(0, 1, 0),
    alpha: number = (45 * Math.PI) / 180,
    height: number = Number.POSITIVE_INFINITY,
  ) {
    this.origin = origin;
    this.direction = direction;
    this.alpha = alpha;
    this.height = height;

    // Initialize to satisfy TS, overridden in resetCache
    this.tanAlpha = 0;
    this.sinAlpha = 0;
    this.cosAlpha = 0;

    this.resetCache();
  }

  resetCache(): void {
    this.bottomCenter_ = null;
    this.bottomHullPoint_ = null;
    this.center_ = null;
    this.sphere_ = null;
    this.line_ = null;
    this.tanAlpha = Math.tan(this.alpha);
    this.sinAlpha = Math.sin(this.alpha);
    this.cosAlpha = Math.cos(this.alpha);
  }

  set(origin: Vector3, direction: Vector3, alpha: number, height: number): this {
    this.origin.copy(origin);
    this.direction.copy(direction);
    this.alpha = alpha;
    this.height = height;
    this.resetCache();
    return this;
  }

  setComponents(
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    alpha: number,
    height: number,
  ): this {
    this.origin.set(ox, oy, oz);
    this.direction.set(dx, dy, dz);
    this.alpha = alpha; // Fixed bug: was this.radius = alpha
    this.height = height;
    this.resetCache();
    return this;
  }

  setFromCameraForTouch(coords: Vector2, touchRadiusInPixel = 10): this {
    const raycaster = new Raycaster();
    raycaster.setFromCamera(coords, STAGE.camera);
    this.origin = raycaster.ray.origin;
    this.direction = raycaster.ray.direction;
    this.height = Number.POSITIVE_INFINITY;
    // const alpha = (touchRadiusInPixel / STAGE.canvas.height) * STAGE.camera.fov;
    this.alpha = ((0.00125 * Math.PI) / 180) * touchRadiusInPixel;
    this.resetCache();
    return this;
  }

  setFromTopCenterBottomCenterBottomHullPoint(
    topCenter: Vector3,
    bottomCenter: Vector3,
    bottomHullPoint: Vector3,
  ): this {
    this.origin.copy(topCenter);
    this.direction.subVectors(bottomCenter, topCenter);
    this.height = this.direction.length();
    this.direction.normalize();
    this.alpha = v3().copy(bottomHullPoint).sub(topCenter).angleTo(this.direction);
    this.resetCache();
    return this;
  }

  clone(): Cone {
    return new Cone(this.origin.clone(), this.direction.clone(), this.alpha, this.height);
  }

  copy(cone: Cone): this {
    return this.set(cone.origin, cone.direction, cone.alpha, cone.height);
  }

  topCenter(): Vector3 {
    return this.origin;
  }

  bottomCenter(): Vector3 {
    if (!this.bottomCenter_) {
      this.bottomCenter_ = this.direction.clone().multiplyScalar(this.height).add(this.origin);
    }
    return this.bottomCenter_;
  }

  bottomHullPoint(): Vector3 {
    if (!this.bottomHullPoint_) {
      this.bottomHullPoint_ = Maths3.getAnyOrthonormalVector(this.direction)
        .multiplyScalar(this.height * Math.tan(this.alpha))
        .add(this.origin);
    }
    return this.bottomHullPoint_;
  }

  center(): Vector3 {
    if (!this.center_) {
      this.center_ = this.direction
        .clone()
        .multiplyScalar(this.height / 2)
        .add(this.origin);
    }
    return this.center_;
  }

  getBoundingSphere(): Sphere {
    if (!this.sphere_) {
      this.sphere_ = new Sphere(this.center(), this.center().distanceTo(this.bottomHullPoint()));
    }
    return this.sphere_;
  }

  getLine(): Line3 {
    if (!this.line_) {
      this.line_ = new Line3(this.topCenter(), this.bottomCenter());
    }
    return this.line_;
  }

  setAlpha(alpha: number): this {
    this.alpha = alpha;
    this.resetCache();
    return this;
  }

  setHeight(height: number): this {
    this.height = height;
    this.resetCache();
    return this;
  }

  setDirection(direction: Vector3): this {
    this.direction = direction;
    this.resetCache();
    return this;
  }

  setOrigin(origin: Vector3): this {
    this.origin = origin;
    this.resetCache();
    return this;
  }

  radius(atLength: number): number {
    return this.tanAlpha * atLength;
  }

  radiusBottom(): number {
    return this.radius(this.height);
  }

  applyMatrix4(matrix: Matrix4): this {
    this.direction.add(this.origin).applyMatrix4(matrix);
    this.origin.applyMatrix4(matrix);
    this.direction.sub(this.origin);
    this.direction.normalize();
    const scale = matrix.getMaxScaleOnAxis();
    this.height = this.height * scale;
    this.resetCache();
    return this;
  }

  translate(offset: Vector3): this {
    this.origin.add(offset);
    this.resetCache();
    return this;
  }

  equals(cone: Cone): boolean {
    return (
      this.alpha === cone.alpha &&
      this.height === cone.height &&
      this.origin.equals(cone.origin) &&
      this.direction.equals(cone.direction)
    );
  }

  isIntersectionSphere(sphere: Sphere): boolean {
    const invSin = 1 / this.sinAlpha;
    const cosSqr = Math.pow(this.cosAlpha, 2);
    const CmV = ConeStatics.CmV.subVectors(sphere.center, this.origin);
    const D = ConeStatics.D.copy(this.direction)
      .multiplyScalar(sphere.radius * invSin)
      .add(CmV);
    let DSqrLen = D.lengthSq();
    let e = D.dot(this.direction);
    if (e > 0 && e * e >= DSqrLen * cosSqr) {
      const sinSqr = Math.pow(this.sinAlpha, 2);
      DSqrLen = CmV.lengthSq();
      e = -CmV.dot(this.direction);
      if (e > 0 && e * e >= DSqrLen * sinSqr) {
        const rSqr = sphere.radius * sphere.radius;
        return DSqrLen <= rSqr;
      }
      return true;
    }
    return false;
  }

  get length() {
    return this.height;
  }
}
